import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import {
  Car,
  Headphones,
  Loader2,
  MessageSquare,
  Package,
  RefreshCw,
  Search,
  Shield,
  ShoppingCart,
  User,
  Users,
} from "lucide-react";
import { ConsoleShell } from "@/components/ConsoleShell";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TABS = [
  { id: "overview", label: "Overview", icon: Shield },
  { id: "customers", label: "Customers", icon: Users },
  { id: "chats", label: "Marketplace chats", icon: MessageSquare },
  { id: "orders", label: "Orders", icon: ShoppingCart },
  { id: "inquiries", label: "Inquiries", icon: Package },
];

function RoleBadge({ role }) {
  const cls =
    role === "admin"
      ? "border-mclaren text-mclaren"
      : role === "sales"
        ? "border-cyan-electric text-cyan-electric"
        : "border-white/20 text-white/50";
  return (
    <span className={`font-mono-tech text-[10px] uppercase px-2 py-0.5 border ${cls}`}>
      {role || "user"}
    </span>
  );
}

function ChatMessages({ messages }) {
  return (
    <div className="space-y-2 max-h-96 overflow-y-auto border border-white/10 p-3 bg-black/30">
      {(messages || []).map((m, i) => (
        <div
          key={i}
          className={`text-xs whitespace-pre-wrap leading-relaxed ${
            m.role === "user" ? "text-mclaren/90" : "text-white/75"
          }`}
        >
          <span className="font-mono-tech text-[8px] uppercase text-white/30">
            {m.role === "user" ? "customer" : m.from_sales ? "sales" : "agent"}:
          </span>{" "}
          {m.content}
        </div>
      ))}
      {!messages?.length && <p className="text-xs text-white/35">No messages</p>}
    </div>
  );
}

export default function AdminPlatform() {
  const [tab, setTab] = useState("overview");
  const [dash, setDash] = useState(null);
  const [users, setUsers] = useState([]);
  const [orders, setOrders] = useState([]);
  const [inquiries, setInquiries] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const [userSearch, setUserSearch] = useState("");
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [customer, setCustomer] = useState(null);
  const [customerLoading, setCustomerLoading] = useState(false);
  const [editName, setEditName] = useState("");
  const [editRole, setEditRole] = useState("user");
  const [editPlan, setEditPlan] = useState("free");

  const [chatSearch, setChatSearch] = useState("");
  const [selectedSessionId, setSelectedSessionId] = useState(null);
  const [sessionDetail, setSessionDetail] = useState(null);
  const [sessionLoading, setSessionLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [d, u, o, inq, sess] = await Promise.all([
        axios.get(`${API}/admin/dashboard`).then((r) => r.data),
        axios.get(`${API}/admin/users?limit=200`).then((r) => r.data).catch(() => []),
        axios.get(`${API}/admin/orders`).then((r) => r.data).catch(() => []),
        axios.get(`${API}/admin/inquiries`).then((r) => r.data).catch(() => []),
        axios.get(`${API}/admin/sessions?limit=100`).then((r) => r.data).catch(() => []),
      ]);
      setDash(d);
      setUsers(u || []);
      setOrders(o || []);
      setInquiries(inq || []);
      setSessions(sess || []);
    } catch {
      setDash(null);
    } finally {
      setLoading(false);
    }
  }, []);

  const searchUsers = useCallback(async (q) => {
    setBusy(true);
    try {
      const params = q.trim() ? `?q=${encodeURIComponent(q.trim())}&limit=200` : "?limit=200";
      const { data } = await axios.get(`${API}/admin/users${params}`);
      setUsers(data || []);
    } finally {
      setBusy(false);
    }
  }, []);

  const loadCustomer = useCallback(async (userId) => {
    if (!userId) {
      setCustomer(null);
      return;
    }
    setCustomerLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/users/${userId}`);
      setCustomer(data);
      setEditName(data.name || "");
      setEditRole(data.role || "user");
      setEditPlan(data.plan || "free");
    } catch {
      setCustomer(null);
    } finally {
      setCustomerLoading(false);
    }
  }, []);

  const loadSession = useCallback(async (sessionId) => {
    if (!sessionId) {
      setSessionDetail(null);
      return;
    }
    setSessionLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/sessions/${sessionId}`);
      setSessionDetail(data);
    } catch {
      setSessionDetail(null);
    } finally {
      setSessionLoading(false);
    }
  }, []);

  const searchChats = useCallback(async (q) => {
    setBusy(true);
    try {
      const params = new URLSearchParams({ limit: "100" });
      if (q.trim()) params.set("q", q.trim());
      const { data } = await axios.get(`${API}/admin/sessions?${params.toString()}`);
      setSessions(data || []);
    } finally {
      setBusy(false);
    }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 20000);
    return () => clearInterval(t);
  }, [load]);

  useEffect(() => {
    if (selectedUserId) loadCustomer(selectedUserId);
  }, [selectedUserId, loadCustomer]);

  useEffect(() => {
    if (selectedSessionId) loadSession(selectedSessionId);
  }, [selectedSessionId, loadSession]);

  const saveCustomerProfile = async () => {
    if (!selectedUserId) return;
    setBusy(true);
    try {
      const { data } = await axios.patch(`${API}/admin/users/${selectedUserId}`, {
        name: editName.trim(),
        role: editRole,
        plan: editPlan,
      });
      setCustomer(data);
      await load();
      await searchUsers(userSearch);
    } finally {
      setBusy(false);
    }
  };

  const openCustomerFromSession = (userId, sessionId) => {
    setTab("customers");
    setSelectedUserId(userId);
    if (sessionId) {
      setSelectedSessionId(sessionId);
      setTab("chats");
    }
  };

  const stats = dash?.stats || {};
  const salesStats = dash?.sales_summary || {};

  return (
    <ConsoleShell title="admin">
      <div className="mx-auto max-w-[1600px] px-4 sm:px-6 lg:px-10 py-6 pb-20">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
          <div>
            <span className="chip-brand">/ admin platform · full control</span>
            <h1 className="mt-3 font-display text-3xl sm:text-4xl font-semibold tracking-tighter flex items-center gap-3">
              <Shield className="h-8 w-8 text-mclaren" />
              Mymech Admin
            </h1>
            <p className="mt-2 text-sm text-white/50 max-w-xl">
              Customers, marketplace chats, vehicles, profiles, orders - full platform oversight.
            </p>
          </div>
          <div className="flex gap-2">
            <Link
              to="/console/sales"
              className="h-10 px-4 border border-cyan-electric/40 text-cyan-electric font-mono-tech text-[10px] uppercase flex items-center gap-2 hover:bg-cyan-electric/10"
            >
              <Headphones className="h-4 w-4" /> Sales desk
            </Link>
            <button
              type="button"
              onClick={load}
              disabled={loading}
              className="h-10 px-4 border border-white/15 font-mono-tech text-[10px] uppercase flex items-center gap-2"
            >
              <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} /> Refresh
            </button>
          </div>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3 mb-6">
          {[
            { label: "Users", val: stats.users, icon: Users },
            { label: "Vehicles", val: stats.vehicles, icon: Car },
            { label: "Chats", val: stats.total_sessions, icon: MessageSquare },
            { label: "Active chats", val: stats.active_sessions, icon: MessageSquare },
            { label: "Orders", val: stats.orders, icon: ShoppingCart },
            { label: "Support open", val: stats.support_open, icon: Headphones },
            { label: "Hot leads", val: salesStats.hot_leads, icon: User },
          ].map(({ label, val, icon: Icon }) => (
            <div key={label} className="border border-white/10 bg-obsidian/60 p-4">
              <Icon className="h-4 w-4 text-mclaren mb-2" />
              <div className="font-display text-2xl">{val ?? "-"}</div>
              <div className="font-mono-tech text-[9px] uppercase text-white/40">{label}</div>
            </div>
          ))}
        </div>

        <div className="flex flex-wrap gap-2 mb-4">
          {TABS.map((t) => (
            <button
              key={t.id}
              type="button"
              onClick={() => setTab(t.id)}
              className={`h-9 px-4 font-mono-tech text-[10px] uppercase tracking-widest border flex items-center gap-2 ${
                tab === t.id ? "border-mclaren text-mclaren bg-mclaren/10" : "border-white/10 text-white/50"
              }`}
            >
              <t.icon className="h-3.5 w-3.5" />
              {t.label}
            </button>
          ))}
        </div>

        {loading && !dash ? (
          <div className="py-20 flex justify-center">
            <Loader2 className="h-8 w-8 animate-spin text-mclaren" />
          </div>
        ) : (
          <>
            {tab === "overview" && (
              <div className="grid lg:grid-cols-2 gap-6">
                <div className="border border-white/10 p-5">
                  <h2 className="font-display text-xl mb-4">Platform health</h2>
                  <div className="space-y-2 text-sm text-white/60">
                    <p>Total marketplace chats: {stats.total_sessions ?? 0}</p>
                    <p>Active sessions: {stats.active_sessions ?? 0}</p>
                    <p>Support handoffs open: {stats.support_open ?? 0}</p>
                    <p>Stock queue pending: {stats.pending_stock ?? 0}</p>
                    <p>Orders completed today: {stats.completed_today ?? 0}</p>
                    <p>Registered vehicles: {stats.vehicles ?? 0}</p>
                  </div>
                </div>
                <div className="border border-white/10 p-5">
                  <h2 className="font-display text-xl mb-4">Recent signups</h2>
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {(dash?.recent_users || []).map((u) => (
                      <button
                        key={u.id}
                        type="button"
                        onClick={() => {
                          setSelectedUserId(u.id);
                          setTab("customers");
                        }}
                        className="w-full text-left border border-white/10 p-3 text-sm hover:border-mclaren/40"
                      >
                        <div className="font-display">{u.name || u.email}</div>
                        <div className="text-xs text-white/40">{u.email}</div>
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {tab === "customers" && (
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 min-h-[560px]">
                <div className="lg:col-span-4 border border-white/10 bg-obsidian/40 flex flex-col max-h-[75vh]">
                  <div className="p-4 border-b border-white/10">
                    <form
                      onSubmit={(e) => {
                        e.preventDefault();
                        searchUsers(userSearch);
                      }}
                      className="flex gap-2"
                    >
                      <div className="relative flex-1">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-white/30" />
                        <input
                          value={userSearch}
                          onChange={(e) => setUserSearch(e.target.value)}
                          placeholder="Search name or email…"
                          className="w-full h-10 bg-black/50 border border-white/15 pl-9 pr-3 text-sm"
                        />
                      </div>
                      <button type="submit" disabled={busy} className="h-10 px-3 bg-mclaren text-void font-mono-tech text-[10px] uppercase">
                        Go
                      </button>
                    </form>
                  </div>
                  <div className="flex-1 overflow-y-auto divide-y divide-white/5">
                    {users.map((u) => (
                      <button
                        key={u.id}
                        type="button"
                        onClick={() => setSelectedUserId(u.id)}
                        className={`w-full text-left p-4 hover:bg-white/5 ${
                          selectedUserId === u.id ? "bg-mclaren/5 border-l-2 border-mclaren" : ""
                        }`}
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0">
                            <div className="font-display truncate">{u.name || "-"}</div>
                            <div className="text-xs text-white/45 truncate">{u.email}</div>
                          </div>
                          <div className="text-right">
                            <RoleBadge role={u.role} />
                            <div className="mt-1 font-mono-tech text-[9px] uppercase text-white/35">
                              {u.plan || "free"}
                            </div>
                          </div>
                        </div>
                        <div className="mt-2 flex gap-3 font-mono-tech text-[9px] uppercase text-white/35">
                          <span>{u.vehicle_count ?? 0} cars</span>
                          <span>{u.session_count ?? 0} chats</span>
                          <span>{u.order_count ?? 0} orders</span>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>

                <div className="lg:col-span-8 border border-white/10 bg-obsidian/60 flex flex-col max-h-[75vh] overflow-y-auto">
                  {!selectedUserId && (
                    <div className="flex-1 flex items-center justify-center text-white/35 text-sm p-8">
                      Select a customer to view profile, vehicles, and marketplace chats.
                    </div>
                  )}
                  {selectedUserId && customerLoading && (
                    <div className="flex justify-center py-16">
                      <Loader2 className="h-6 w-6 animate-spin text-mclaren" />
                    </div>
                  )}
                  {selectedUserId && customer && !customerLoading && (
                    <div className="p-5 space-y-6">
                      <div className="flex flex-wrap items-start justify-between gap-4">
                        <div>
                          <h2 className="font-display text-2xl">{customer.name}</h2>
                          <p className="text-sm text-white/50">{customer.email}</p>
                          <p className="mt-1 font-mono-tech text-[10px] uppercase text-white/35">
                            Joined {customer.created_at?.slice(0, 10) || "-"} · ID {customer.id}
                          </p>
                        </div>
                        <RoleBadge role={customer.role} />
                      </div>

                      <div className="border border-mclaren/30 bg-mclaren/5 p-4 space-y-3">
                        <h3 className="font-mono-tech text-[10px] uppercase text-mclaren">Edit profile</h3>
                        <div className="grid sm:grid-cols-2 gap-3">
                          <label className="block">
                            <span className="font-mono-tech text-[9px] uppercase text-white/40">Display name</span>
                            <input
                              value={editName}
                              onChange={(e) => setEditName(e.target.value)}
                              className="mt-1 w-full h-10 bg-black/50 border border-white/15 px-3 text-sm"
                            />
                          </label>
                          <label className="block">
                            <span className="font-mono-tech text-[9px] uppercase text-white/40">Role</span>
                            <select
                              value={editRole}
                              onChange={(e) => setEditRole(e.target.value)}
                              className="mt-1 w-full h-10 bg-black/50 border border-white/15 px-3 text-sm"
                            >
                              <option value="user">user</option>
                              <option value="sales">sales</option>
                              <option value="admin">admin</option>
                            </select>
                          </label>
                          <label className="block sm:col-span-2">
                            <span className="font-mono-tech text-[9px] uppercase text-white/40">Plan</span>
                            <select
                              value={editPlan}
                              onChange={(e) => setEditPlan(e.target.value)}
                              className="mt-1 w-full h-10 bg-black/50 border border-white/15 px-3 text-sm"
                            >
                              <option value="free">free · 5 chats</option>
                              <option value="pro">pro · 50 chats</option>
                              <option value="fleet">fleet · 100 chats</option>
                            </select>
                          </label>
                        </div>
                        <button
                          type="button"
                          disabled={busy}
                          onClick={saveCustomerProfile}
                          className="h-10 px-5 bg-mclaren text-void font-mono-tech text-[10px] uppercase"
                        >
                          Save changes
                        </button>
                      </div>

                      <div>
                        <h3 className="font-display text-lg mb-3 flex items-center gap-2">
                          <Car className="h-5 w-5 text-cyan-electric" />
                          Registered vehicles ({customer.stats?.vehicles ?? 0})
                        </h3>
                        {(customer.vehicles || []).length === 0 ? (
                          <p className="text-sm text-white/40">No vehicles registered.</p>
                        ) : (
                          <div className="grid sm:grid-cols-2 gap-3">
                            {(customer.vehicles || []).map((v) => (
                              <div key={v.id} className="border border-white/10 p-4">
                                <div className="font-display">
                                  {v.year} {v.brand} {v.model}
                                  {v.is_primary && (
                                    <span className="ml-2 font-mono-tech text-[9px] uppercase text-cyan-electric">
                                      primary
                                    </span>
                                  )}
                                </div>
                                <div className="text-xs text-white/45 mt-1">
                                  {v.trim || "-"} · {v.mileage?.toLocaleString() ?? 0} mi
                                </div>
                                <div className="font-mono-tech text-[9px] uppercase text-white/35 mt-2">
                                  VIN: {v.vin || "not set"} · Health {v.health_score ?? "-"}
                                </div>
                                {v.nickname && <div className="text-xs text-white/50 mt-1">"{v.nickname}"</div>}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>

                      <div>
                        <h3 className="font-display text-lg mb-3 flex items-center gap-2">
                          <MessageSquare className="h-5 w-5 text-mclaren" />
                          Marketplace chats ({customer.stats?.sessions ?? 0})
                        </h3>
                        <div className="space-y-2">
                          {(customer.recent_sessions || []).map((s) => (
                            <button
                              key={s.session_id}
                              type="button"
                              onClick={() => {
                                setSelectedSessionId(s.session_id);
                                setTab("chats");
                              }}
                              className="w-full text-left border border-white/10 p-3 hover:border-mclaren/40"
                            >
                              <div className="flex justify-between gap-2">
                                <span className="font-mono-tech text-[10px] uppercase text-white/50">
                                  {s.stage?.replace(/_/g, " ")} · {s.active ? "live" : "closed"}
                                </span>
                                <span className="text-[9px] text-white/35">{s.updated_at?.slice(0, 16)}</span>
                              </div>
                              <p className="text-sm text-white/70 mt-1 line-clamp-2">{s.last_message || "-"}</p>
                            </button>
                          ))}
                        </div>
                      </div>

                      <div>
                        <h3 className="font-display text-lg mb-3 flex items-center gap-2">
                          <ShoppingCart className="h-5 w-5 text-cyan-electric" />
                          Orders ({customer.stats?.orders ?? 0})
                        </h3>
                        <div className="space-y-2">
                          {(customer.recent_orders || []).map((o) => (
                            <div key={o.id} className="border border-white/10 p-3 text-sm">
                              <div className="font-display">{o.part_name}</div>
                              <div className="text-xs text-white/45 font-mono-tech uppercase">
                                {o.status} · ${o.total_usd || "quote"} · {o.created_at?.slice(0, 10)}
                              </div>
                            </div>
                          ))}
                          {!customer.recent_orders?.length && (
                            <p className="text-sm text-white/40">No orders yet.</p>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {tab === "chats" && (
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 min-h-[560px]">
                <div className="lg:col-span-5 border border-white/10 bg-obsidian/40 flex flex-col max-h-[75vh]">
                  <div className="p-4 border-b border-white/10">
                    <form
                      onSubmit={(e) => {
                        e.preventDefault();
                        searchChats(chatSearch);
                      }}
                      className="flex gap-2"
                    >
                      <div className="relative flex-1">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-white/30" />
                        <input
                          value={chatSearch}
                          onChange={(e) => setChatSearch(e.target.value)}
                          placeholder="Search customer, part, stage…"
                          className="w-full h-10 bg-black/50 border border-white/15 pl-9 pr-3 text-sm"
                        />
                      </div>
                      <button type="submit" disabled={busy} className="h-10 px-3 bg-mclaren text-void font-mono-tech text-[10px] uppercase">
                        Go
                      </button>
                    </form>
                  </div>
                  <div className="flex-1 overflow-y-auto divide-y divide-white/5">
                    {sessions.map((s) => (
                      <button
                        key={s.session_id}
                        type="button"
                        onClick={() => setSelectedSessionId(s.session_id)}
                        className={`w-full text-left p-4 hover:bg-white/5 ${
                          selectedSessionId === s.session_id ? "bg-mclaren/5 border-l-2 border-mclaren" : ""
                        }`}
                      >
                        <div className="font-display text-sm">{s.user?.name || s.user?.email || "Customer"}</div>
                        <div className="text-xs text-white/45">{s.user?.email}</div>
                        <p className="text-xs text-white/60 mt-1 line-clamp-2">{s.preview || s.last_message}</p>
                        <div className="mt-2 font-mono-tech text-[9px] uppercase text-white/35">
                          {s.stage?.replace(/_/g, " ")} · {s.active ? "live" : "closed"} · {s.message_count} msgs
                        </div>
                      </button>
                    ))}
                  </div>
                </div>

                <div className="lg:col-span-7 border border-white/10 bg-obsidian/60 flex flex-col max-h-[75vh]">
                  {!selectedSessionId && (
                    <div className="flex-1 flex items-center justify-center text-white/35 text-sm p-8">
                      Select a chat to read the full marketplace conversation.
                    </div>
                  )}
                  {selectedSessionId && sessionLoading && (
                    <div className="flex justify-center py-16">
                      <Loader2 className="h-6 w-6 animate-spin text-mclaren" />
                    </div>
                  )}
                  {selectedSessionId && sessionDetail && !sessionLoading && (
                    <div className="flex-1 overflow-y-auto p-5 space-y-4">
                      <div className="flex flex-wrap justify-between gap-3">
                        <div>
                          <button
                            type="button"
                            onClick={() => openCustomerFromSession(sessionDetail.user?.id)}
                            className="font-display text-xl hover:text-mclaren text-left"
                          >
                            {sessionDetail.user?.name || sessionDetail.user?.email}
                          </button>
                          <p className="text-sm text-white/50">{sessionDetail.user?.email}</p>
                        </div>
                        <div className="text-right font-mono-tech text-[10px] uppercase text-white/40">
                          {sessionDetail.stage?.replace(/_/g, " ")} · {sessionDetail.active !== false ? "live" : "closed"}
                        </div>
                      </div>

                      <div className="grid sm:grid-cols-2 gap-3 text-sm">
                        <div className="border border-white/10 p-3">
                          <div className="font-mono-tech text-[9px] uppercase text-white/40">Phone</div>
                          {sessionDetail.customer_full?.phone || sessionDetail.customer?.phone || "-"}
                        </div>
                        <div className="border border-white/10 p-3">
                          <div className="font-mono-tech text-[9px] uppercase text-white/40">Part request</div>
                          {sessionDetail.customer_full?.part_query || sessionDetail.customer?.part_query || "-"}
                        </div>
                      </div>

                      {sessionDetail.listing && (
                        <div className="border border-cyan-electric/30 p-4">
                          <div className="font-mono-tech text-[10px] uppercase text-cyan-electric">
                            {sessionDetail.listing.sku}
                          </div>
                          <div className="font-display text-lg">{sessionDetail.listing.name}</div>
                          <div className="text-cyan-electric">${sessionDetail.listing.price_usd || "quote"}</div>
                        </div>
                      )}

                      {sessionDetail.order && (
                        <div className="border border-white/10 p-3 text-sm">
                          <div className="font-mono-tech text-[9px] uppercase text-white/40">Order</div>
                          {sessionDetail.order.part_name} · {sessionDetail.order.status} · $
                          {sessionDetail.order.total_usd || "quote"}
                        </div>
                      )}

                      <div>
                        <div className="font-mono-tech text-[10px] uppercase text-white/40 mb-2">
                          Full conversation
                        </div>
                        <ChatMessages messages={sessionDetail.messages} />
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {tab === "orders" && (
              <div className="space-y-2 max-h-[600px] overflow-y-auto">
                {orders.map((o) => (
                  <div key={o.id} className="border border-white/10 p-4 flex flex-wrap justify-between gap-2">
                    <div>
                      <button
                        type="button"
                        onClick={() => o.user?.id && openCustomerFromSession(o.user.id)}
                        className="font-display hover:text-mclaren text-left"
                      >
                        {o.user?.name || o.contact_name}
                      </button>
                      <div className="font-display text-white/80">{o.part_name}</div>
                      <div className="text-xs text-white/45 font-mono-tech uppercase">
                        {o.sku} · {o.contact_name} · {o.phone}
                      </div>
                    </div>
                    <div className="text-right font-mono-tech text-[10px] uppercase text-white/50">
                      {o.status} · ${o.total_usd || "quote"}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {tab === "inquiries" && (
              <div className="space-y-2 max-h-[600px] overflow-y-auto">
                {inquiries.map((inq) => (
                  <div key={inq.id} className="border border-white/10 p-4">
                    <div className="flex justify-between gap-2">
                      <button
                        type="button"
                        onClick={() => inq.user?.id && openCustomerFromSession(inq.user.id)}
                        className="font-mono-tech text-[10px] uppercase text-cyan-electric hover:text-white"
                      >
                        {inq.user?.name || inq.user?.email || "Customer"}
                      </button>
                      <span className="font-mono-tech text-[10px] uppercase text-white/40">{inq.status}</span>
                    </div>
                    <p className="mt-1 text-sm text-white/70">{inq.message}</p>
                    <div className="mt-1 text-xs text-white/40">
                      {inq.sku || "no sku"} · {inq.created_at?.slice(0, 19)}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </ConsoleShell>
  );
}
